import { Paper, Typography, Box } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

interface Props {
  data: { name: string; value: number; color: string }[];
}

/**
 * 📈 채널별 유입 비율 도넛 차트
 * - 대시보드 ChannelBreakdownChart와 동일한 셸, 색상은 채널 사전(entry.color) 사용
 */
const InflowShareChart = ({ data }: Props) => {
  const filtered = data.filter((d) => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Paper elevation={0} sx={{
      p: { xs: 2, md: 3 }, borderRadius: 3,
      border: '1px solid #e5e7eb', height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#0f172a', mb: 0.5 }}>
        채널별 유입 비율
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        선택 기간 내 접수 건 기준
      </Typography>
      <Box sx={{ flex: 1, position: 'relative', minHeight: 200 }}>
        {total === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color="text.secondary" fontSize="0.85rem">데이터가 없습니다</Typography>
          </Box>
        ) : (
          <>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={filtered}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                  labelLine={false}
                >
                  {filtered.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 10, border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.85rem',
                  }}
                  formatter={(value: number, name: string) => [`${value}건 (${Math.round((value / total) * 100)}%)`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* 중앙 총 건수 */}
            <Box sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center', pointerEvents: 'none',
            }}>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                {total}
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>
                총 건수
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* 하단 범례 (건수 + 비중) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, pt: 1.5, borderTop: '1px solid #f0f0f0', maxHeight: 220, overflowY: 'auto' }}>
        {filtered.map((entry) => (
          <Box key={entry.name} display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={0.8}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: entry.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>{entry.name}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.8}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{entry.value}건</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                {total > 0 ? `${Math.round((entry.value / total) * 100)}%` : '0%'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default InflowShareChart;
